
























































  import Debug from 'debug'
  import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
  import DOMPurify from 'dompurify';
  import { TranslateResult } from 'vue-i18n';
  import i18n from '@/plugins/i18n';
  import QuestionHeaderComponent from './question-header-component.vue';
  import { Container, inject } from 'inversify';
  import { lazyInject } from '@/app/container';
  import * as Api from "@/app/question/components/api";
  import { QuestionFactorySymbol, QuestionSolutionFactorySymbol, QuestionFactory,
           QuestionSolutionFactory, CurrentQuestion } from './api/question-factory';
  import { QuestionOptionsFactory, QuestionOptionsFactorySymbol } from './question-options-factory';

export interface Question {
  id: string;
  type: string;
  question: string;
  content: string;
  feedback: {
    correct: string
    incorrect: string
    tryAgain: string
  }
}

export type QuestionMode = Api.QuestionMode

export interface QuestionAnswer {
  answer: unknown
}

export interface QuestionState {
  state: unknown
}

export type QuestionStateChangedPayload = QuestionState

export interface QuestionEvaluationResult {
  score: number
  result: Api.QuestionEvaluationResultType
}

export interface QuestionAnsweredPayload {
  answer: QuestionAnswer
  evaluation: {
    score: number
  }
}

const debug = Debug('question-component')

  @Component({
    components: {
      QuestionHeaderComponent
    }
  })
  export default class QuestionComponent2 extends Vue {
    @Prop({ required: true })
    public question: Question;

    @Prop({ required: true })
    public mode: QuestionMode

    @Prop({ required: true })
    public evaluated: boolean;

    @Prop({ required: true })
    public showEvaluateButton: boolean;

    @Prop({ required: true })
    public canEvaluate: boolean;

    @Prop({ required: true })
    public questionAnswer: QuestionAnswer | null;

    @Prop({ required: true })
    public questionState: QuestionState | null;

    @Prop({ required: true })
    public evaluationResult: QuestionEvaluationResult | null

    @Prop({ required: true })
    public showSolution: boolean;

    @Prop({ required: true })
    public showButtons: boolean;

    private currentQuestionComponent: CurrentQuestion<unknown, unknown> | null = null;

    private currentInstructionKey: string = ''

    private currentQuestionOptions: Api.QuestionOptions<unknown> = Api.defaultQuestionOptions<unknown>()

    @lazyInject(QuestionFactorySymbol)
    private questionFactory: QuestionFactory<unknown, unknown, unknown, unknown>;

    @lazyInject(QuestionSolutionFactorySymbol)
    private questionSolutionFactory: QuestionSolutionFactory<unknown, unknown>;

    @lazyInject(QuestionOptionsFactorySymbol)
    private questionOptionsFactory: QuestionOptionsFactory<unknown>;

    @Watch('question.id')
    private changeQuestionType() {
      debug('changeQuestionType: ', this.questionState, 'answer: ', this.questionAnswer)

      this.loadQuestionComponent();
    }

    @Watch('evaluated')
    private evaluatedWatcher() {
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$props.evaluated = this.evaluated;
      }
    }

    @Watch('questionAnswer')
    private answerWatcher(newValue: QuestionAnswer | null) {
      debug('answerWatcher', newValue);
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$props.answer = newValue;
      }
    }

    @Watch('questionState')
    private stateWatcher(newValue: QuestionState | null) {
      debug('stateWatcher', newValue);
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$props.state = newValue;
      }
    }

    @Watch('showSolution')
    private showSolutionWatcher(newValue: boolean) {
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$props.showSolution = newValue;
      }
    }

    @Watch('evaluationResult')
    private evaluationResultWatcher(newValue: QuestionEvaluationResult | null,
                                    oldValue: QuestionEvaluationResult | null) {

      if ((!newValue && !oldValue) || (newValue && oldValue && newValue.result === oldValue.result)) { return }

      // one of them is null or changed
      debug('evaluationResultWatcher', newValue);
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$props.evaluationResultType = this.mapResultType(this.evaluationResult)
      }
    }

    private loadQuestionComponent() {
      const container = <HTMLElement>this.$refs['question-component-container'];

      container.innerHTML = '';

      this.removeComponentEmitters();

      const resultType = this.mapResultType(this.evaluationResult)
      this.currentQuestionComponent = this.questionFactory(this.question.type, {
        question: this.question,
        mode: this.mode,
        answer: this.questionAnswer,
        evaluated: this.evaluated,
        showSolution: this.showSolution,
        state: this.questionState,
        evaluationResultType: resultType,
        showButtons: this.showButtons,
      });
      this.currentQuestionOptions = this.questionOptionsFactory(this.question.type)
      const customInstruction = this.currentQuestionOptions.customInstruction(this.question.content)
      this.currentInstructionKey = customInstruction || this.question.type

      debug('current question options', this.currentQuestionOptions)

      // REVIEW: it would be better if not the same component would be used with different question instances
      // instead new question should be created
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$on('answer-changed', this.answerChanged);
        this.currentQuestionComponent.$on('state-changed', this.stateChanged);
        this.currentQuestionComponent.$on('self-evaluated', this.emitEvaluated);
        this.currentQuestionComponent.$on('self-reset', this.emitReset);

        debug(`mounting '${this.question.type}' question with state`, this.questionState,
              'answer: ', this.questionAnswer,
              'evaluationResultType', resultType,
              'mode', this.mode)

        this.currentQuestionComponent.$mount();
        container.appendChild(this.currentQuestionComponent.$el);

        if (this.showSolution) {
          this.$nextTick(() => {
            this.loadQuestionSolutionComponent();
          });
        }
      }
    }

    private mapResultType(result: QuestionEvaluationResult | null): Api.QuestionEvaluationResultType {
      return result ? result.result : 'not-evaluated'
    }

    private loadQuestionSolutionComponent() {
      const container = <HTMLElement>this.$refs['question-solution-component-container'];

      if (container) {
        container.innerHTML = '';

        if (this.currentQuestionComponent && this.currentQuestionComponent.solutionProps) {
          const solutionProps = this.currentQuestionComponent.solutionProps();

          const solutionComponent = this.questionSolutionFactory(this.question.type, {
            userAnswer: solutionProps.userAnswer,
            correctAnswer: solutionProps.correctAnswer,
          });
          solutionComponent.$mount();

          container.appendChild(solutionComponent.$el);
        }
      }
    }

    private mounted() {
      debug('mounted, state: ', this.questionState, 'answer: ', this.questionAnswer)

      this.loadQuestionComponent();
    }

    private removeComponentEmitters() {
      if (this.currentQuestionComponent) {
        this.currentQuestionComponent.$off([
          "answer-changed",
          "state-changed",
          "solution-props-changed",
          "self-evaluated"
        ]);
        this.currentQuestionComponent.$destroy();
      }
    }

    private beforeDestroy() {
      this.removeComponentEmitters();
    }

    private get questionText(): string {
      return DOMPurify.sanitize(this.question.question, { ALLOWED_TAGS: [] }).trim();
    }

    private get correct(): boolean {
      if (!this.evaluationResult) {
        return false
      }

      return this.evaluationResult.result === 'correct'
    }

    private get partiallyCorrect(): boolean {
      if (!this.evaluationResult) {
        return false
      }

      return this.evaluationResult.result === 'partially-correct'
    }

    private get evaluatedAndIncorrect(): boolean {
      return this.evaluated && !this.correct;
    }

    private get hasAnyAnswer(): boolean {
      return this.questionAnswer !== null
    }

    private t(key: string): TranslateResult {
      return i18n.t(key);
    }

    private get answer() {
      if (!this.questionAnswer) {
        return null
      }
      return this.questionAnswer.answer
    }

    private answerChanged(params: { answer: unknown, correct: boolean, score: number}) {
      const answer: QuestionAnswer = {
        answer: params.answer
      }

      const evaluation = {
        score: params.score
      }

      const payload: QuestionAnsweredPayload = {
        answer,
        evaluation
      }
      this.emitAnswerChanged(payload);
    }

    private stateChanged(payload: QuestionStateChangedPayload) {
      debug('stateChanged', payload)
      this.emitStateChanged(payload);
    }

    @Emit('answer-changed')
    private emitAnswerChanged(payload: QuestionAnsweredPayload): QuestionAnsweredPayload {
      return payload;
    }

    @Emit('state-changed')
    private emitStateChanged(
      payload: QuestionStateChangedPayload): QuestionStateChangedPayload {
      return payload;
    }

    private handleCheckClicked(): void {
      debug('handleEvaluateClicked')
      this.emitEvaluated();
    }

    @Emit('evaluated')
    private emitEvaluated() {
      debug('emitEvaluated')
    }

    private handleResetClicked(): void {
      debug('handleResetClicked')
      this.emitReset()
    }

    @Emit('reset')
    private emitReset() {
      debug('emitReset')
    }

    private handleShowSolutionClicked(): void {
      debug('handleShowSolutionClicked')
      this.emitShowSolution()

      this.$nextTick(() => {
        this.loadQuestionSolutionComponent();
      });
    }

    @Emit('show-solution')
    private emitShowSolution() {
      debug('emitShowSolution')
    }
  }
